import { Autocomplete, Box, FormControl, Stack, Typography } from "@mui/material";
import { InputBase } from "components/common/Input";
import { cancelRequest, api } from "services/api.service";
import { Pickup } from "types/pickup";
import { formatDate } from "utils/helpers";
import axios from "axios";
import { useEffect, useMemo, useRef, useState } from "react";

const DEBOUNCE_MS = 1000;
const MIN_CHARS = 3;
const CANCEL_KEY = "pickup-quick-filter-autocomplete";

function mergePickupMaps(a: Map<number, Pickup>, list: Pickup[]) {
  const next = new Map(a);
  list.forEach((p) => next.set(p.id, p));
  return next;
}

export function PickupQuickFilterAutocomplete({
  pickupIds,
  setPickupIds,
  pickupCatalog,
  size = "medium",
}: {
  pickupIds: string[];
  setPickupIds: (ids: string[]) => void;
  pickupCatalog: Pickup[];
  size?: "small" | "medium";
}) {
  const [inputValue, setInputValue] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [searchOptions, setSearchOptions] = useState<Pickup[]>([]);
  const [pickupById, setPickupById] = useState<Map<number, Pickup>>(() => new Map());
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedQuery(inputValue), DEBOUNCE_MS);
    return () => window.clearTimeout(t);
  }, [inputValue]);

  useEffect(() => {
    setPickupById((prev) => mergePickupMaps(prev, pickupCatalog));
  }, [pickupCatalog]);

  useEffect(() => {
    const q = debouncedQuery.trim();
    if (q.length < MIN_CHARS) {
      cancelRequest(CANCEL_KEY);
      setSearchOptions([]);
      setLoading(false);
      return;
    }

    let ignore = false;
    setLoading(true);

    const filterSegment = `order_number,contains,${q}`;
    const listUrl = `/pickups?page[number]=1&page[size]=25&filter[]=${encodeURIComponent(filterSegment)}`;

    api
      .get(listUrl, { cancelKey: CANCEL_KEY } as any)
      .then((res) => {
        if (ignore) return;
        const list = (res.data as { pickups: Pickup[] }).pickups;
        setSearchOptions(list);
        setPickupById((prev) => mergePickupMaps(prev, list));
      })
      .catch((err) => {
        if (axios.isCancel(err) || ignore) return;
        setSearchOptions([]);
      })
      .finally(() => {
        if (!ignore) setLoading(false);
      });

    return () => {
      ignore = true;
    };
  }, [debouncedQuery]);

  const selectedPickups = useMemo(() => {
    return pickupIds.map((idStr) => {
      const id = Number(idStr);
      const fromMap = pickupById.get(id);
      if (fromMap) return fromMap;
      return {
        id,
        orderNumber: id,
      } as Pickup;
    });
  }, [pickupIds, pickupById]);

  const pickupByIdRef = useRef(pickupById);
  pickupByIdRef.current = pickupById;

  useEffect(() => {
    const missing = pickupIds.filter((idStr) => !pickupByIdRef.current.has(Number(idStr)));
    if (missing.length === 0) return;

    const filterParam = `id,is_any_of,${missing.join(",")}`;
    api
      .get(`/pickups?page[number]=1&page[size]=50&filter[]=${encodeURIComponent(filterParam)}`)
      .then((res) => {
        setPickupById((prev) => mergePickupMaps(prev, (res.data as { pickups: Pickup[] }).pickups));
      })
      .catch(() => {});
  }, [pickupIds]);

  const getOptionLabel = (option: Pickup) => {
    return String(option.orderNumber);
  };

  return (
    <FormControl variant="standard" size={size} fullWidth>
      <Stack direction="row" alignItems="flex-start" gap={0.5} sx={{ minWidth: 0, width: "100%" }}>
        <Autocomplete
          multiple
          disableCloseOnSelect
          forcePopupIcon={false}
          fullWidth
          size={size}
          options={searchOptions}
          value={selectedPickups}
          inputValue={inputValue}
          onInputChange={(_, value, reason) => {
            if (reason === "reset") return;
            setInputValue(value);
          }}
          onChange={(_, newValue) => {
            setPickupIds(newValue.map((p) => p.id.toString()));
            setPickupById((prev) => mergePickupMaps(prev, newValue));
            setInputValue("");
            cancelRequest(CANCEL_KEY);
            setSearchOptions([]);
            setLoading(false);
          }}
          loading={loading}
          filterOptions={(opts) => opts}
          isOptionEqualToValue={(a, b) => a.id === b.id}
          getOptionLabel={getOptionLabel}
          renderOption={(props, option) => (
            <li {...props} key={option.id}>
              <Box>
                <>
                  <Typography variant="body2">{option.orderNumber}</Typography>
                  {option.scheduledAt ? (
                    <Typography variant="caption" color="text.secondary" display="block">
                      {formatDate(option.scheduledAt)}
                    </Typography>
                  ) : null}
                </>
              </Box>
            </li>
          )}
          renderInput={(params) => <InputBase {...params} placeholder={pickupIds.length === 0 ? "Pickup ID" : ""} />}
          noOptionsText={
            inputValue.trim().length < MIN_CHARS ? `Enter at least ${MIN_CHARS} characters` : "No pickups found"
          }
        />
      </Stack>
    </FormControl>
  );
}
